import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { MoreHorizontal, Pencil, RefreshCw, ChevronDown, Calendar, Target, Building2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { CreateLoaderTable } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Goal, GoalStatus, GoalFilters } from "@/types/goal";
import {
  getGoals,
  deleteGoal,
  toggleGoalStatus,
  getGoalStatuses,
} from "@/Api/goals";
import { getSelectUnities } from "@/Api/unities";
import { Mask } from "@/utils/mask";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { AddGoal } from "../Add";
import { UpdateGoal } from "../Update";
import { Main } from "@/components/Main";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

interface TableDataProps extends Goal {}

interface ExtendedPageActions extends TypePageActions {
  refresh?: () => Promise<void>;
}

interface Unity {
  id: number;
  name: string;
}

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
  onEditGoal,
  goalType = "meta",
}: {
  dataApi: ApiDataProps<TableDataProps>;
  pageActions: ExtendedPageActions;
  updateFront: any;
  onEditGoal: (uuid: string) => void;
  goalType?: "meta" | "super_meta";
}) => {
  const { hasPermission } = usePermissions();

  const handleToggleStatus = async (uuid: string) => {
    try {
      const response = await toggleGoalStatus(uuid);
      if (response.ok) {
        toast.success("Status da meta atualizado!");
        if (pageActions.refresh) {
          await pageActions.refresh();
        }
      } else {
        toast.error("Erro ao atualizar status");
      }
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const formatDateBR = (dateString: string) => {
    if (!dateString) return "";
    // Se já tem "T", não adicionar novamente
    const dateStr = dateString.includes("T")
      ? dateString.split("T")[0]
      : dateString;
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const getTargetValue = (goal: Goal) => {
    if (goalType === "super_meta") {
      // Prioriza a soma dos vendedores, mas só se for maior que 0
      const sellersSum = Number(goal.goal_sellers_sum_super_target_value) || 0;
      if (sellersSum > 0) {
        return sellersSum;
      }
      // Fallback para super_target_value da meta (metas sem vendedores)
      return Number(goal.super_target_value) || 0;
    }
    return goal.target_value || 0;
  };

  const calculateProgress = (goal: Goal) => {
    const target = getTargetValue(goal);
    if (!target || target === 0) return 0;
    const sold = goal.sold_value || 0;
    const progress = Math.min(100, Math.round((sold / target) * 100));
    return isNaN(progress) ? 0 : progress;
  };

  const calculateRemaining = (goal: Goal) => {
    const target = getTargetValue(goal);
    const sold = goal.sold_value || 0;
    const remaining = Math.max(0, target - sold);
    return isNaN(remaining) ? 0 : remaining;
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader className="bg-primary/10 dark:bg-primary/20">
          <TableRow>
            <TableHead className="text-primary font-semibold">Unidade</TableHead>
            <TableHead className="text-primary font-semibold">Período</TableHead>
            <TableHead className="text-right text-primary font-semibold">
              {goalType === "super_meta" ? "Super Meta" : "Meta"}
            </TableHead>
            <TableHead className="text-right text-primary font-semibold">
              {goalType === "super_meta" ? "Super Meta 100%" : "Meta 100%"}
            </TableHead>
            <TableHead className="text-right text-primary font-semibold">Vendido</TableHead>
            <TableHead className="text-right text-primary font-semibold">Falta Vender</TableHead>
            <TableHead className="text-right text-primary font-semibold">Média/Dia</TableHead>
            <TableHead className="text-right text-primary font-semibold">Média Necessária</TableHead>
            <TableHead className="text-primary font-semibold">Status</TableHead>
            <TableHead className="text-center text-primary font-semibold">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item) => {
            const progress = calculateProgress(item);
            return (
              <TableRow key={item?.id}>
                <TableCell className="font-medium">
                  {item?.unity?.name}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col text-sm">
                    <span>{formatDateBR(item?.start_date)}</span>
                    <span className="text-muted-foreground">
                      até {formatDateBR(item?.end_date)}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {Mask.formatCurrency(getTargetValue(item))}
                </TableCell>
                <TableCell className="text-right font-mono text-amber-600">
                  {Mask.formatCurrency(
                    goalType === "super_meta"
                      ? item?.super_target_100_value || 0
                      : item?.target_100_value || 0
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-col items-end">
                    <span className="font-mono text-green-600">
                      {Mask.formatCurrency(item?.sold_value || 0)}
                    </span>
                    <div className="w-20 h-2 bg-gray-200 rounded-full mt-1">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {progress}%
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono text-orange-600">
                  {Mask.formatCurrency(calculateRemaining(item))}
                </TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">
                  {Mask.formatCurrency(item?.daily_average || 0)}
                </TableCell>
                <TableCell className="text-right font-mono text-blue-600">
                  {Mask.formatCurrency(item?.required_daily_average || 0)}
                </TableCell>
                <TableCell>
                  <Badge
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: item?.goal_status?.color,
                      color: "white",
                    }}
                    onClick={() => handleToggleStatus(item?.uuid)}
                  >
                    {item?.goal_status?.name}
                  </Badge>
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-2 w-full justify-center">
                    <If condition={hasPermission("edit-goal")}>
                      <Button
                        size="sm"
                        onClick={() => onEditGoal(item?.uuid)}
                        className="h-8 w-8 p-0 bg-blue-500 hover:bg-blue-600 text-white"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </If>
                    <If condition={hasPermission("delete-goal")}>
                      <DeleteBtn
                        params={{
                          func: deleteGoal,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev: any) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data: any) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={`Meta de ${item?.unity?.name}`}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {(!dataApi?.data || dataApi?.data?.length === 0) && (
            <TableRow>
              <TableCell colSpan={10} className="text-center py-8">
                Nenhuma meta encontrada
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};

type DateFilterType = "today" | "yesterday" | "week" | "month" | "custom" | null;

const periods = [
  { key: "today", label: "Hoje" },
  { key: "yesterday", label: "Ontem" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mês" },
];

export function GoalsList() {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [filters, setFilters] = useState<GoalFilters>({});
  const [unities, setUnities] = useState<Unity[]>([]);
  const [, setStatuses] = useState<GoalStatus[]>([]);
  const [editGoalUuid, setEditGoalUuid] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilterType>(null);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [goalType, setGoalType] = useState<"meta" | "super_meta">("meta");
  const [selectedUnityIds, setSelectedUnityIds] = useState<string[]>([]);
  const { hasPermission } = usePermissions();

  const fetchData = async (page: number = 1, currentFilters = filters) => {
    setState("loading");
    const { data } = await getGoals(page, currentFilters as any);
    setListData(data?.response);
    setState("production");
  };

  const loadFiltersData = async () => {
    try {
      const [unitiesRes, statusesRes] = await Promise.all([
        getSelectUnities(),
        getGoalStatuses(),
      ]);
      if (unitiesRes.data?.response) {
        setUnities(unitiesRes.data.response);
      }
      if (statusesRes.data?.response) {
        setStatuses(statusesRes.data.response);
      }
    } catch (error) {
      console.error("Erro ao carregar filtros:", error);
    }
  };

  useEffect(() => {
    fetchData(1, filters);
    loadFiltersData();
  }, []);

  const getDateRange = (type: DateFilterType): { start_date?: string; end_date?: string } => {
    const today = new Date();
    const formatDate = (date: Date) => date.toISOString().split("T")[0];

    switch (type) {
      case "today":
        return { start_date: formatDate(today), end_date: formatDate(today) };
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { start_date: formatDate(yesterday), end_date: formatDate(yesterday) };
      }
      case "week": {
        const startOfWeek = new Date(today);
        const dayOfWeek = today.getDay();
        const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startOfWeek.setDate(today.getDate() - diff);
        return { start_date: formatDate(startOfWeek), end_date: formatDate(today) };
      }
      case "month": {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start_date: formatDate(startOfMonth), end_date: formatDate(today) };
      }
      case "custom":
        return {
          start_date: customStartDate || undefined,
          end_date: customEndDate || undefined,
        };
      default:
        return {};
    }
  };

  const handleDateFilterChange = async (type: DateFilterType) => {
    setDateFilter(type);

    const dateRange = getDateRange(type);
    const newFilters = {
      ...filters,
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    };
    setFilters(newFilters);
    await fetchData(1, newFilters);
  };

  const handleApplyCustomDate = async () => {
    if (!customStartDate || !customEndDate) return;
    setDateFilter("custom");
    const newFilters = {
      ...filters,
      start_date: customStartDate,
      end_date: customEndDate,
    };
    setFilters(newFilters);
    setIsDatePopoverOpen(false);
    await fetchData(1, newFilters);
  };

  const toggleUnity = (unityId: string) => {
    let newSelected: string[];
    if (selectedUnityIds.includes(unityId)) {
      newSelected = selectedUnityIds.filter(id => id !== unityId);
    } else {
      newSelected = [...selectedUnityIds, unityId];
    }
    setSelectedUnityIds(newSelected);
    const newFilters = {
      ...filters,
      unity_ids: newSelected.length > 0 ? newSelected.map(Number) : undefined,
    };
    setFilters(newFilters);
    fetchData(1, newFilters);
  };

  const toggleAllUnities = () => {
    let newSelected: string[];
    if (selectedUnityIds.length === unities.length) {
      newSelected = [];
    } else {
      newSelected = unities.map(u => String(u.id));
    }
    setSelectedUnityIds(newSelected);
    const newFilters = {
      ...filters,
      unity_ids: newSelected.length > 0 ? newSelected.map(Number) : undefined,
    };
    setFilters(newFilters);
    fetchData(1, newFilters);
  };

  const getUnityDisplayText = () => {
    if (selectedUnityIds.length === 0 || selectedUnityIds.length === unities.length) {
      return "Todas unidades";
    }
    return `${selectedUnityIds.length} unidade${selectedUnityIds.length > 1 ? "s" : ""}`;
  };

  const getCurrentPeriodLabel = () => {
    if (dateFilter === "custom" && customStartDate && customEndDate) {
      const formatDate = (dateStr: string) => {
        const [, month, day] = dateStr.split("-");
        return `${day}/${month}`;
      };
      return `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`;
    }
    return periods.find(p => p.key === dateFilter)?.label || "Período";
  };

  const hasUnitySelection = selectedUnityIds.length > 0 && selectedUnityIds.length < unities.length;


  const pageActions: ExtendedPageActions = {
    refresh: async () => {
      await fetchData(listData?.current_page || 1, filters);
    },
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        await fetchData(listData?.current_page + 1, filters);
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        await fetchData(listData?.current_page - 1, filters);
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        await fetchData(listData?.last_page, filters);
      }
      return;
    },
  };

  const handleEditGoal = (uuid: string) => {
    setEditGoalUuid(uuid);
  };

  const handleCloseEdit = () => {
    setEditGoalUuid(null);
  };

  return (
    <Main>
        <PageHeader
          icon={Target}
          title="Metas de Vendas"
          description="Gerencie as metas da sua equipe"
        >
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => pageActions.refresh?.()}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
            <If condition={hasPermission("create-goal")}>
              <AddGoal
                updateFront={setListData}
                dataApi={listData}
                refreshList={pageActions.refresh}
              />
            </If>
          </div>
        </PageHeader>

      {/* Filtros estilo Dashboard */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm mb-4">
        {/* Unidade */}
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <Popover>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex items-center justify-between min-w-[140px] sm:min-w-[160px] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-all",
                  hasUnitySelection
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
              >
                <div className="flex items-center min-w-0">
                  <Building2 className="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">{getUnityDisplayText()}</span>
                </div>
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ml-1 sm:ml-2" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <div className="max-h-[300px] overflow-y-auto">
                <div
                  className="flex items-center space-x-2 px-3 py-2 border-b bg-gray-50 dark:bg-zinc-800 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700"
                  onClick={toggleAllUnities}
                >
                  <Checkbox
                    checked={selectedUnityIds.length === unities.length && unities.length > 0}
                    className={cn(
                      selectedUnityIds.length > 0 && selectedUnityIds.length < unities.length &&
                      "data-[state=unchecked]:bg-primary/50 data-[state=unchecked]:border-primary"
                    )}
                  />
                  <span className="text-sm font-medium">Todas as unidades</span>
                </div>
                {unities.map((unity) => {
                  const isSelected = selectedUnityIds.includes(String(unity.id));
                  return (
                    <div
                      key={unity.id}
                      className={cn(
                        "flex items-center space-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-700",
                        isSelected && "bg-primary/5"
                      )}
                      onClick={() => toggleUnity(String(unity.id))}
                    >
                      <Checkbox checked={isSelected} />
                      <span className="text-sm">{unity.name}</span>
                    </div>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Período */}
        <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-9 sm:h-10 gap-1.5 sm:gap-2 px-2 sm:px-3 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 text-xs sm:text-sm"
            >
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
              {getCurrentPeriodLabel()}
              <ChevronDown className="h-3 w-3 text-gray-400" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-4" align="start">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {periods.map((p) => (
                  <Button
                    key={p.key}
                    variant={dateFilter === p.key ? "default" : "outline"}
                    size="sm"
                    className="h-9"
                    onClick={() => {
                      handleDateFilterChange(p.key as DateFilterType);
                      setIsDatePopoverOpen(false);
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Período personalizado</p>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-[130px] h-9"
                  />
                  <Input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-[130px] h-9"
                  />
                </div>
                <Button
                  className="w-full mt-3 h-9"
                  size="sm"
                  onClick={handleApplyCustomDate}
                  disabled={!customStartDate || !customEndDate}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Separador */}
        <div className="h-8 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />

        {/* Meta / Super Meta */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 sm:ml-auto">
          <button
            onClick={() => setGoalType("meta")}
            className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all ${
              goalType === "meta"
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            Meta
          </button>
          <button
            onClick={() => setGoalType("super_meta")}
            className={`px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-all ${
              goalType === "super_meta"
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            Super Meta
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {state === "loading" ? (
          <CreateLoaderTable cols={10} lines={6} />
        ) : (
          <TableData
            dataApi={listData}
            pageActions={pageActions}
            updateFront={setListData}
            onEditGoal={handleEditGoal}
            goalType={goalType}
          />
        )}
      </div>

      {editGoalUuid && hasPermission("edit-goal") && (
        <UpdateGoal
          uuid={editGoalUuid}
          isOpen={!!editGoalUuid}
          onClose={handleCloseEdit}
          onUpdate={pageActions.refresh}
        />
      )}
    </Main>
  );
}
